import isEmpty from 'lodash/isEmpty'
import * as React from 'react'
import { Mutation, MutationFunction } from 'react-apollo'

import Form from 'main/components/common/Form'
import { UPDATE_USER_MUTATION, UpdateUser, UpdateUserVariables, USER_QUERY } from 'main/gql/user'
import { Button, colors, spacing, Text } from 'main/ui'
import PasswordSection, { initialParams, IParams, IValidation, validate } from './PasswordSection'

interface IProps {
  cta: string
  onComplete: () => void
}

export default function PasswordOnlyForm(props: IProps) {
  const { cta, onComplete } = props

  const [params, setParams] = React.useState<IParams>(initialParams())
  const [validation, setValidation] = React.useState<IValidation>({})
  const [error, setError] = React.useState<boolean>(false)

  return (
    <Mutation<UpdateUser, UpdateUserVariables>
      mutation={UPDATE_USER_MUTATION}
      onError={() => setError(true)}
      refetchQueries={[{ query: USER_QUERY }]}
    >
      {(mutate, { loading }) => (
        <Form onSubmit={() => handleSubmit(params, setValidation, setError, mutate, onComplete)}>
          <div css={{ marginBottom: spacing.majorScale(error ? 2 : 4) }}>
            <PasswordSection
              params={params}
              validation={validation}
              onChange={changes => handleParamsChange(changes, setParams)}
            />
          </div>

          {error && (
            <div css={{ marginBottom: spacing.majorScale(2) }}>
              <Text css={{ color: colors.text.error }}>
                Something went wrong! Please try again.
              </Text>
            </div>
          )}

          <Button kind="regular" disabled={loading} full>
            {loading ? 'Submitting...' : cta}
          </Button>
        </Form>
      )}
    </Mutation>
  )
}

function handleParamsChange(
  changes: Partial<IParams>,
  setParams: React.Dispatch<React.SetStateAction<IParams>>,
) {
  setParams(params => ({ ...params, ...changes }))
}

function handleSubmit(
  params: IParams,
  setValidation: (validation: IValidation) => void,
  setError: (error: boolean) => void,
  mutate: MutationFunction<UpdateUser, UpdateUserVariables>,
  onComplete: () => void,
) {
  const validation = validate(params)
  setError(false)
  setValidation(validation)
  if (!isEmpty(validation)) {
    return
  }

  mutate({
    variables: {
      user: {
        password: params.password.trim(),
      },
    },
  }).then(() => onComplete())
}
